/**
 * Hook gắn nhận diện khuôn mặt vào video webcam đang chạy (TT 83 C3-b).
 * Nạp detector lười (chunk riêng, chỉ khi đang thi + flag bật). Nạp lỗi → im lặng,
 * KHÔNG sinh vi phạm giả.
 */
import { type RefObject, useEffect, useRef } from "react";

import {
  type FaceCounter,
  type FaceViolation,
  FaceViolationTracker,
  SAMPLE_INTERVAL_MS,
  loadFaceDetector,
} from "./faceMonitor";

export function useFaceDetection(
  videoRef: RefObject<HTMLVideoElement | null>,
  active: boolean,
  onViolation: (v: FaceViolation) => void,
  load: () => Promise<FaceCounter> = loadFaceDetector,
) {
  const callback = useRef(onViolation);
  callback.current = onViolation;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let counter: FaceCounter | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    const tracker = new FaceViolationTracker();

    load()
      .then((c) => {
        if (cancelled) {
          c.close();
          return;
        }
        counter = c;
        timer = setInterval(() => {
          const video = videoRef.current;
          if (!video || video.readyState < 2 || !counter) return;
          const now = Date.now(); // MediaPipe chỉ cần timestamp ms tăng dần
          const violation = tracker.sample(counter.count(video, now), now);
          if (violation) callback.current(violation);
        }, SAMPLE_INTERVAL_MS);
      })
      .catch((err) => {
        console.warn("[proctoring] không nạp được bộ nhận diện khuôn mặt", err);
      });

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      counter?.close();
    };
  }, [active, videoRef, load]);
}
